

/*
Usage Notes:

This is the landing page plan picker component. It's a reusable component that works with any product.
Update offers or presentation here without affecting the default product page component.

IMPORTANT: This component uses a DIFFERENT tag name (purolabs-plan-picker-landing) to avoid conflicts
with the main product page plan picker (purolabs-plan-picker-component.js).

  <purolabs-plan-picker-landing
    product='{{ product | json | escape }}'
    selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
    sellingPlanGroupId='{{ selling_plan_group_id }}'
    sellingPlanIds='["plan_id_1", "plan_id_2", "plan_id_3"]'
    directToCheckout='false'
    >

    <input type="hidden" name="selling_plan" form="{{ product_form_id }}" />
  </purolabs-plan-picker-landing>
  <script src="{{ 'purolabs-plan-picker-landing.js' | asset_url }}" type="module"></script>

  Note: The form attribute on the selling plan input is not required if element is inside of a form already.
*/

// Lit is self-hosted next to this file (assets/lit-core.min.js), preloaded in layout/theme.liquid
import { LitElement, html, css } from './lit-core.min.js';

const ALLOWED_SHOPIFY_PLAN_ID_LIST = ['2368045314', '2368078082', '2368110850'];
const ALLOWED_SHOPIFY_PLAN_IDS = new Set(ALLOWED_SHOPIFY_PLAN_ID_LIST);

// Data structure for quantity/discount tiers
// 3 tiers: 1 bottle, 3 bottles, 6 bottles (landing page specific pricing)
// Gifts: 1 bottle=1 gift, 3 bottles=2 gifts, 6 bottles=4 gifts
const boxes = {
    oneTime: {
        1: { quantity: 1, discount: 0, gifts: 0, badge: null, featured: null },
        2: { quantity: 3, discount: 17.15, gifts: 0, badge: '17% OFF', featured: 'MOST POPULAR' },
        3: { quantity: 6, discount: 33, gifts: 0, badge: '33% OFF', featured: 'BEST VALUE' }
    },
    subscription: {
        1: { quantity: 1, discount: 20, gifts: 1, badge: '20% OFF', featured: null },
        2: { quantity: 3, discount: 30, gifts: 2, badge: '30% OFF', featured: 'MOST POPULAR' },
        3: { quantity: 6, discount: 45, gifts: 4, badge: '45% OFF', featured: 'BEST VALUE' }
    }
};

// Default placeholder gifts - will be overridden by section settings (4 gifts max)
const DEFAULT_PLACEHOLDER_IMAGE = 'https://cdn.shopify.com/s/files/1/0495/5724/2023/files/gift-placeholder.png?v=1';
const MAX_GIFTS = 4;
const defaultGifts = [
    { id: 1, name: 'Frother', value: 19, image: DEFAULT_PLACEHOLDER_IMAGE, variantId: null },
    { id: 2, name: 'Gummies', value: 20, image: DEFAULT_PLACEHOLDER_IMAGE, variantId: null },
    { id: 3, name: 'Tin', value: 15, image: DEFAULT_PLACEHOLDER_IMAGE, variantId: null },
    { id: 4, name: 'Calendar', value: 15, image: DEFAULT_PLACEHOLDER_IMAGE, variantId: null }
];

/* ==========================================================================
   PUROLABS PLAN PICKER - PRODUCT PAGE MATCHING DESIGN
   Matches the proven product page design with compact toggle and product images
   ========================================================================== */

const planPickerStyle = css`
    :host {
        /* Product colors - use metafield variables from section for dynamic theming */
        --product-primary: var(--skio-colour6, #9B2C3D);
        --product-secondary: var(--skio-colour, #044436);
        --product-accent: var(--skio-colour3, #F5F0FA);

        /* System colors */
        --puro-dark: #101820;
        --puro-gray-700: #374151;
        --puro-gray-500: #6b7280;
        --puro-gray-400: #9ca3af;
        --puro-gray-300: #d1d5db;
        --puro-gray-200: #e5e7eb;
        --puro-gray-100: #f3f4f6;
        --puro-white: #ffffff;
        --puro-border: #374151;
        --puro-radius: 16px;
        --puro-radius-sm: 10px;

        font-family: var(--text-font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif);
        display: block;
        padding: 0;
        max-width: 100%;
        overflow: hidden;
        box-sizing: border-box;
    }

    /* ==========================================================================
       MAIN WRAPPER
       ========================================================================== */
    .plan-picker {
        background: var(--puro-white);
        border-radius: var(--puro-radius);
        padding: clamp(8px, 3vw, 20px);
        box-sizing: border-box;
        max-width: 100%;
    }

    /* ==========================================================================
       PURCHASE TYPE TOGGLE - Compact style matching product page
       ========================================================================== */
    .purchase-toggle {
        display: flex;
        flex-direction: row;
        gap: 13px;
        margin-bottom: 0;
        align-items: stretch;
    }

    .toggle-option {
        flex: 1;
        display: flex;
        align-items: flex-start;
        padding: 10px 14px;
        border: 1px solid #BFBFBF;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        background: var(--puro-white);
        position: relative;
    }

    .toggle-option:hover {
        border-color: var(--puro-dark);
    }

    .toggle-option.active {
        border-color: var(--product-primary);
        border-width: 2px;
        background: var(--puro-white);
    }

    /* Most Popular badge above toggle */
    .toggle-popular-badge {
        position: absolute;
        top: -12px;
        left: 50%;
        transform: translateX(-50%);
        background: var(--product-primary);
        color: var(--puro-white);
        font-size: 11px;
        font-weight: 700;
        padding: 4px 12px;
        border-radius: 12px;
        text-transform: lowercase;
        white-space: nowrap;
    }

    .toggle-radio {
        width: 22px;
        height: 22px;
        min-width: 22px;
        border: 2px solid #BFBFBF;
        border-radius: 50%;
        position: relative;
        margin-right: 10px;
        transition: all 0.2s ease;
        background: var(--puro-white);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .toggle-option.active .toggle-radio {
        border-color: #101820;
        border-width: 2px;
    }

    .toggle-option.active .toggle-radio::after {
        content: '';
        width: 12px;
        height: 12px;
        background: #101820;
        border-radius: 50%;
    }

    .toggle-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .toggle-label {
        font-size: clamp(15px, 4.5vw, 18px);
        font-weight: 700;
        color: var(--puro-dark);
        line-height: 1.2;
    }

    .toggle-price {
        font-size: clamp(12px, 3vw, 14px);
        font-weight: 600;
        color: var(--product-primary);
        line-height: 1.2;
    }

    .toggle-price.onetime {
        color: var(--puro-dark);
    }

    .toggle-price s {
        font-weight: 400;
        color: var(--puro-gray-400);
        margin-right: 4px;
        font-size: 12px;
    }

    .toggle-gifts {
        font-size: clamp(11px, 3vw, 13px);
        color: #8BA54C;
        font-weight: 700;
        line-height: 1.2;
    }

    .toggle-badge {
        display: none;
    }

    /* ==========================================================================
       UPSELL BANNER - For One-Time Tab (Arrow pointing to toggle)
       ========================================================================== */
    .upsell-banner {
        background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe3 100%);
        border: 2px dashed #8BA54C;
        border-radius: 12px;
        padding: 14px 20px;
        margin-top: 16px;
        margin-bottom: 0;
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .upsell-arrow {
        color: #8BA54C;
        animation: bounce-up 1s ease-in-out infinite;
        flex-shrink: 0;
    }

    @keyframes bounce-up {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-4px); }
    }

    .upsell-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .upsell-heading {
        font-weight: 600;
        font-size: clamp(13px, 3.5vw, 16px);
        color: var(--puro-dark);
        line-height: 1.35;
    }

    .upsell-subtext {
        font-size: clamp(11px, 3vw, 14px);
        color: var(--puro-gray-600);
        line-height: 1.4;
        margin-top: 2px;
    }

    .upsell-highlight {
        color: var(--product-primary);
        font-weight: 700;
    }

    .upsell-highlight.green {
        color: #8BA54C;
        white-space: nowrap;
    }

    .upsell-icon {
        display: none;
    }

    .upsell-text {
        display: none;
    }

    /* ==========================================================================
       QUANTITY SECTION - 3 Column Clean Layout (Matching Product Page)
       ========================================================================== */
    .quantity-section {
        margin-bottom: 20px;
    }

    .section-title {
        font-weight: 700;
        font-size: clamp(16px, 4.5vw, 18px);
        line-height: 1.3;
        padding: clamp(12px, 4vw, 20px) 0;
        color: var(--puro-dark);
        margin: 0;
    }

    .section-title strong {
        font-weight: 700;
    }

    /* Quantity Grid - 3 Column (Matching product page exactly) */
    .quantity-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 13px;
        margin-bottom: 20px;
        align-items: start;
    }

    .qty-option {
        display: flex;
        flex-direction: column;
        width: 100%;
        position: relative;
        border-radius: 8px;
        background-color: #fff;
        border: 1px solid #101820;
        cursor: pointer;
        overflow: hidden;
    }

    .qty-option:hover {
        border-color: var(--puro-dark);
    }

    .qty-option.selected {
        background-color: #EBF7C3;
    }

    /* Margin compensation for boxes without header/footer to align content */
    .qty-option.no-header {
        margin-top: 30px;
    }

    .qty-option.no-footer {
        margin-bottom: 28px;
    }

    /* Discount header at top (15% OFF, 20% OFF etc.) */
    .qty-discount-header {
        background: var(--product-primary);
        color: #ffffff;
        font-size: 16px;
        font-weight: 700;
        line-height: 16px;
        padding: 7px 0;
        width: 100%;
        text-align: center;
        border-top-left-radius: 7px;
        border-top-right-radius: 7px;
    }

    /* Hide placeholder header completely */
    .qty-discount-header.placeholder {
        display: none;
    }

    .qty-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 12px 0;
        width: 100%;
    }

    /* Main title - X Bottles */
    .qty-title {
        font-size: 18px;
        line-height: 20px;
        font-weight: 600;
        color: #101820;
    }

    /* Subtitle - X day supply */
    .qty-supply {
        font-size: 14px;
        color: #101820;
        font-weight: 400;
        line-height: 16px;
    }

    /* Bottle image */
    .qty-image {
        width: 120px;
        height: auto;
        margin: 0 auto;
        object-fit: contain;
    }

    /* Price */
    .qty-price-each {
        font-size: 16px;
        line-height: 18px;
        font-weight: 500;
        color: #101820;
    }

    .qty-price-each strong {
        font-size: 18px;
        font-weight: 600;
    }

    /* Featured badges at bottom (MOST POPULAR / BEST VALUE) */
    .qty-featured {
        text-align: center;
        color: #fff;
        font-size: 16px;
        font-weight: 500;
        line-height: 28px;
        width: 100%;
        background: #8BA54C;
    }

    .qty-featured.best-value {
        background: #044436;
    }

    /* Hide placeholder badge - allows 1-bottle box to be shorter */
    .qty-featured.placeholder {
        display: none;
    }

    /* Hide old savings badge - we now use discount header */
    .qty-savings {
        display: none;
    }

    /* ==========================================================================
       FREE GIFTS SECTION - 4-column single row layout with overlay value badges
       ========================================================================== */
    .gifts-section {
        margin-bottom: 12px;
        padding: 16px 0;
        background: transparent;
        border-radius: var(--puro-radius);
        border: none;
        border-top: 1px solid var(--puro-gray-200);
        margin-top: 12px;
    }

    .gifts-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 14px;
        flex-wrap: wrap;
        gap: 6px;
    }

    .gifts-title {
        font-size: 17px;
        font-weight: 700;
        color: var(--puro-dark);
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .gifts-count {
        font-size: 15px;
        color: var(--product-primary);
        font-weight: 700;
    }

    .gifts-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
    }

    .gift-item {
        position: relative;
        border: 2px dashed var(--product-primary);
        border-radius: 12px;
        padding: 0;
        text-align: center;
        background: var(--puro-white);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        overflow: hidden;
    }

    .gift-item.locked {
        border-color: var(--puro-gray-300);
        background: var(--puro-gray-100);
    }

    .gift-item.locked .gift-image-wrapper .gift-image {
        filter: grayscale(100%);
        opacity: 0.5;
    }

    .gift-image-wrapper {
        position: relative;
        width: 100%;
        aspect-ratio: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px;
        box-sizing: border-box;
    }

    .gift-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .gift-value {
        position: absolute;
        bottom: 6px;
        left: 6px;
        background: #8BA54C;
        color: var(--puro-white);
        font-size: 9px;
        font-weight: 700;
        padding: 4px 8px;
        border-radius: 6px;
        line-height: 1.2;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .gift-value .value-price {
        font-size: 12px;
        font-weight: 700;
    }

    .gift-value .value-label {
        font-size: 8px;
        font-weight: 600;
    }

    .gift-item.locked .gift-value {
        background: var(--puro-gray-400);
    }

    .gift-lock-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0.7);
        z-index: 2;
    }

    .gift-lock-overlay .lock-icon {
        color: var(--product-primary);
        opacity: 0.8;
    }

    .gift-item.locked .gift-image-wrapper .gift-image {
        filter: grayscale(100%);
        opacity: 0.3;
    }

    .gift-lock {
        display: none;
    }

    .gift-name {
        font-size: 13px;
        color: var(--puro-dark);
        font-weight: 600;
        line-height: 1.3;
        padding: 10px 6px;
    }

    .gift-name .free-label {
        color: var(--product-primary);
        font-weight: 700;
    }

    .gift-item.locked .gift-name {
        color: var(--puro-gray-400);
    }

    .gift-item.locked .gift-name .free-label {
        color: var(--puro-gray-400);
    }

    /* ==========================================================================
       BENEFITS LIST - Compact 2-line layout
       ========================================================================== */
    .benefits-list {
        margin-top: 16px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px 24px;
    }

    .benefit-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;
        color: var(--puro-dark);
    }

    .benefit-item:last-child {
        flex-basis: 100%;
    }

    .benefit-check {
        color: var(--product-primary);
        font-weight: bold;
        font-size: 16px;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .benefit-check svg {
        width: 16px;
        height: 16px;
    }

    /* ==========================================================================
       MOBILE RESPONSIVE (Matching product page exactly)
       ========================================================================== */
    @media only screen and (max-width: 768px) {
        .plan-picker {
            padding: 16px;
        }

        /* toggle-label and toggle-price use clamp() for fluid scaling */

        .toggle-badge {
            font-size: 12px;
            padding: 3px 6px;
        }

        .quantity-grid {
            gap: 10px;
        }

        .qty-title {
            font-size: 17px;
        }

        .qty-supply {
            font-size: 14px;
        }

        .qty-price-each {
            font-size: 16px;
        }

        .qty-price-each strong {
            font-size: 16px;
        }

        .benefits-list {
            gap: 6px 16px;
        }

        .benefit-item {
            font-size: 14px;
        }
    }

    @media only screen and (max-width: 480px) {
        .purchase-toggle {
            gap: 8px;
            margin-bottom: 16px;
        }

        .toggle-option {
            padding: 12px 12px;
        }

        .toggle-badge {
            font-size: 11px;
            padding: 2px 5px;
            margin-left: 5px;
        }

        .toggle-price s {
            display: none;
        }

        .qty-content {
            padding: 10px 0;
        }

        .qty-title {
            font-size: 16px;
            line-height: 17px;
        }

        .qty-supply {
            font-size: 12px;
            line-height: 17px;
        }

        .qty-image {
            width: 90px;
        }

        .qty-price-each {
            font-size: 15px;
            line-height: 16px;
        }

        .qty-price-each strong {
            font-size: 16px;
        }

        .qty-discount-header {
            font-size: 13px;
            padding: 4px 0;
        }

        .qty-featured {
            font-size: 13px;
            line-height: 24px;
        }

        .quantity-grid {
            margin-bottom: 18px;
            gap: 8px;
        }

        .gifts-section {
            padding: 14px 0;
        }

        .gifts-header {
            margin-bottom: 12px;
        }

        .gifts-title {
            font-size: 15px;
        }

        .gifts-count {
            font-size: 14px;
        }

        .gifts-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
        }

        .gift-item {
            border-radius: 10px;
        }

        .gift-image-wrapper {
            padding: 8px;
        }

        .gift-value {
            bottom: 6px;
            left: 6px;
            padding: 4px 6px;
            border-radius: 6px;
        }

        .gift-value .value-price {
            font-size: 11px;
        }

        .gift-value .value-label {
            font-size: 9px;
        }

        .gift-name {
            font-size: 11px;
            padding: 8px 4px;
        }

        .benefits-list {
            gap: 6px 12px;
        }

        .benefit-item {
            font-size: 14px;
        }

        .benefit-check svg {
            width: 14px;
            height: 14px;
        }

        .upsell-banner {
            padding: 12px 14px;
            gap: 10px;
        }

        /* upsell-heading and upsell-subtext use clamp() for fluid scaling */

        .upsell-arrow svg {
            width: 18px;
            height: 18px;
        }
    }

    @media only screen and (max-width: 390px) {
        .purchase-toggle {
            gap: 6px;
        }

        .toggle-option {
            padding: 10px 8px;
        }

        .toggle-badge {
            font-size: 10px;
        }

        .toggle-radio {
            width: 18px;
            height: 18px;
            min-width: 18px;
            margin-right: 8px;
        }

        .toggle-option.active .toggle-radio::after {
            width: 9px;
            height: 9px;
        }

        .quantity-grid {
            gap: 6px;
        }

        .qty-title {
            font-size: 14px;
        }

        .qty-supply {
            font-size: 12px;
        }

        .qty-content {
            padding: 10px 0;
        }

        .qty-image {
            width: 75px;
        }

        .qty-price-each {
            font-size: 13px;
        }

        .qty-price-each strong {
            font-size: 14px;
        }

        .qty-discount-header {
            font-size: 13px;
            padding: 4px 0;
        }

        .qty-featured {
            font-size: 13px;
            line-height: 24px;
        }

        .gifts-title {
            font-size: 14px;
        }

        .gifts-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 4px;
        }

        .gift-item {
            border-radius: 8px;
            border-width: 1.5px;
        }

        .gift-image-wrapper {
            padding: 6px;
        }

        .gift-value {
            bottom: 4px;
            left: 4px;
            padding: 3px 5px;
            border-radius: 5px;
        }

        .gift-value .value-price {
            font-size: 11px;
        }

        .gift-value .value-label {
            font-size: 9px;
        }

        .gift-name {
            font-size: 11px;
            padding: 6px 4px;
        }

        .benefits-list {
            gap: 4px 10px;
        }

        .benefit-item {
            font-size: 13px;
        }

        .benefit-check {
            width: 16px;
            height: 16px;
        }

        .benefit-check svg {
            width: 12px;
            height: 12px;
        }
    }

    @media only screen and (max-width: 360px) {
        .gifts-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
        }

        .gift-image-wrapper {
            padding: 10px;
        }

        .gift-value {
            bottom: 6px;
            left: 6px;
            padding: 4px 6px;
        }

        .gift-name {
            font-size: 11px;
            padding: 8px 6px;
        }
    }

    @media only screen and (max-width: 320px) {
        .purchase-toggle {
            flex-direction: column;
            gap: 8px;
        }

        .toggle-option {
            width: 100%;
            padding: 12px 10px;
        }

        .quantity-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
        }

        .qty-option:last-child {
            grid-column: span 2;
        }

        .qty-image {
            width: 70px;
        }

        .qty-title {
            font-size: 13px;
        }

        .qty-supply {
            font-size: 11px;
        }

        .qty-price-each {
            font-size: 12px;
        }

        .gifts-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
        }

        .gift-name {
            font-size: 11px;
        }
    }

    /* ==========================================================================
       ESTIMATED DELIVERY BADGE
       ========================================================================== */
    .delivery-estimate {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: var(--puro-gray-100);
        border-radius: var(--puro-radius-sm);
        padding: 12px 16px;
        margin-top: 16px;
    }

    .delivery-icon {
        color: var(--product-secondary);
        flex-shrink: 0;
    }

    .delivery-text {
        font-size: 14px;
        font-weight: 500;
        color: var(--puro-dark);
    }

    .delivery-dates {
        color: var(--product-secondary);
        font-weight: 600;
    }
`;

export class PurolabsPlanPickerComponent extends LitElement {

    static properties = {
        product: { type: Object },
        productHandle: { type: String },
        key: { type: String },

        subscriptionFirst: { type: Boolean },
        startSubscription: { type: Boolean },
        hideSelect: { type: Boolean },
        discountFormat: { type: String },

        currency: { type: String },
        language: { type: String },
        moneyFormatter: {},

        selectedVariant: { type: Object },
        skioSellingPlanGroups: {},
        availableSellingPlanGroups: {},
        selectedSellingPlanGroup: {},
        selectedSellingPlan: {},

        // New properties for Elavate-style picker
        purchaseType: { type: String },
        selectedQuantityIndex: { type: Number },

        productMetafields: { type: Object },
        imageMetafields: { type: Object },

        // Gift products from section settings
        giftProducts: { type: Array },
        freeGifts: { type: Array },

        // Dynamic configuration - allows different products/landing pages to customize
        boxesConfig: { type: Object },      // Custom tier configuration
        sellingPlanIds: { type: Array },    // Custom selling plan IDs to allow
        sellingPlanGroupId: { type: String }, // Custom selling plan group ID

        // Checkout behavior
        directToCheckout: { type: Boolean }, // If true, redirect to checkout instead of mini-cart
    };

    static styles = planPickerStyle;

    constructor() {
        super();
        this.availableSellingPlanGroups = [];
        this.product = null;
        this.productHandle = null;
        this.key = null;
        this.startSubscription = true;
        this.subscriptionFirst = true;
        this.hideSelect = false;
        this.discountFormat = 'percent';

        this.currency = window?.Shopify?.currency?.active ?? 'USD';
        this.language = window?.Shopify?.locale ?? 'en-US';
        this.moneyFormatter = new Intl.NumberFormat(this.language, {
            style: 'currency',
            currency: this.currency,
        });

        this.selectedVariant = null;
        this.selectedSellingPlanGroup = null;
        this.selectedSellingPlan = null;

        // New state for Elavate-style picker
        this.purchaseType = 'subscription'; // Default to subscription
        this.selectedQuantityIndex = 3; // Default to 6 bags (most popular)

        this.productMetafields = null;
        this.imageMetafields = null;
        this._requestedProductData = false;

        // Gift products - will be populated from attribute or defaults
        this.giftProducts = null;
        this.freeGifts = [...defaultGifts];

        // Dynamic configuration - null means use defaults
        this.boxesConfig = null;
        this.sellingPlanIds = null;
        this.sellingPlanGroupId = null; // If null, will use first available Skio group from product

        // Checkout behavior - default to false (open mini-cart)
        this.directToCheckout = false;
    }

    // Get effective boxes configuration (custom or default)
    getBoxes() {
        return this.boxesConfig || boxes;
    }

    // Get effective selling plan IDs (custom or default)
    getAllowedSellingPlanIds() {
        if (this.sellingPlanIds && Array.isArray(this.sellingPlanIds)) {
            return new Set(this.sellingPlanIds.map(String));
        }
        return ALLOWED_SHOPIFY_PLAN_IDS;
    }

    // Get effective selling plan ID list (for sorting)
    getAllowedSellingPlanIdList() {
        if (this.sellingPlanIds && Array.isArray(this.sellingPlanIds)) {
            return this.sellingPlanIds.map(String);
        }
        return ALLOWED_SHOPIFY_PLAN_ID_LIST;
    }

    hasSellingPlanData(product) {
        return Array.isArray(product?.selling_plan_groups) && product.selling_plan_groups.length > 0;
    }

    safeParseJSON(value, fallback = {}) {
        if (value == null) return fallback;
        try {
            return JSON.parse(value);
        } catch (error) {
            this.error('Failed to parse JSON attribute', error);
            return fallback;
        }
    }

    parseBooleanAttribute(value, defaultValue = true) {
        if (value == null) return defaultValue;
        const normalized = String(value).toLowerCase();
        if (normalized === 'false') return false;
        if (normalized === 'true') return true;
        return defaultValue;
    }

    ensureProductData() {
        if (this._requestedProductData) return;

        const handle = this.productHandle || this.product?.handle;
        const missingProduct = !this.hasSellingPlanData(this.product);
        const variantMissingAllocations = !this.selectedVariant?.selling_plan_allocations?.length;

        if (!handle || (!missingProduct && !variantMissingAllocations)) {
            return;
        }

        this._requestedProductData = true;
        const activeVariantId = this.selectedVariant?.id;

        this.fetchProduct(handle)
            .then((product) => {
                if (!product) return;
                if (activeVariantId) {
                    const enrichedVariant = product.variants?.find((variant) => String(variant.id) === String(activeVariantId));
                    if (enrichedVariant) {
                        this.selectedVariant = enrichedVariant;
                    }
                }
            })
            .catch((error) => {
                this.error('Failed to load full product data', error);
                this._requestedProductData = false;
            });
    }

    connectedCallback() {
        super.connectedCallback();
        // Coming back from checkout restores this page from the back/forward cache with the button still busy.
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) this.setAddingToCart(false);
        });

        this.productMetafields = this.safeParseJSON(this.getAttribute('metafields'), {});
        this.imageMetafields = this.safeParseJSON(this.getAttribute('imageMetafields'), {});

        // Parse dynamic configuration from attributes
        const boxesConfigAttr = this.getAttribute('boxesConfig');
        if (boxesConfigAttr) {
            this.boxesConfig = this.safeParseJSON(boxesConfigAttr, null);
            this.log('Using custom boxes configuration:', this.boxesConfig);
        }

        const sellingPlanIdsAttr = this.getAttribute('sellingPlanIds');
        if (sellingPlanIdsAttr) {
            this.sellingPlanIds = this.safeParseJSON(sellingPlanIdsAttr, null);
            this.log('Using custom selling plan IDs:', this.sellingPlanIds);
        }

        const sellingPlanGroupIdAttr = this.getAttribute('sellingPlanGroupId');
        if (sellingPlanGroupIdAttr) {
            this.sellingPlanGroupId = sellingPlanGroupIdAttr;
            this.log('Using custom selling plan group ID:', this.sellingPlanGroupId);
        }

        // Parse directToCheckout setting
        const directToCheckoutAttr = this.getAttribute('directToCheckout');
        if (directToCheckoutAttr) {
            this.directToCheckout = this.parseBooleanAttribute(directToCheckoutAttr, false);
            this.log('Direct to checkout:', this.directToCheckout);
        }

        // Parse gift products from section settings
        const giftProductsAttr = this.getAttribute('giftProducts');
        if (giftProductsAttr) {
            const parsedGifts = this.safeParseJSON(giftProductsAttr, []);
            if (parsedGifts.length > 0) {
                // Merge section gift settings with defaults, using actual product images when available
                this.freeGifts = parsedGifts.map((gift, index) => ({
                    id: gift.id || index + 1,
                    name: gift.name || defaultGifts[index]?.name || `Gift ${index + 1}`,
                    value: gift.value || defaultGifts[index]?.value || 0,
                    image: gift.image || DEFAULT_PLACEHOLDER_IMAGE,
                    variantId: gift.variantId || null,
                    productHandle: gift.productHandle || null
                }));
            }
        }

        this.ensureProductData();

        // Intercept the external Add to Cart button for subscription orders with gifts
        this._setupAddToCartInterception();
    }

    _setupAddToCartInterception() {
        // Wait for DOM to be ready
        setTimeout(() => {
            // IMPORTANT:
            // Theme can render the submit button as either #AddToCart (normal flow)
            // or #DirectToCheckout (block setting "direct_to_checkout"). The old
            // #DirectToCheckout handler in assets/custom.js uses /cart/<variant>:1?checkout
            // and drops the current page query string (UTM/fbclid/etc). We intercept both.
            const buttons = Array.from(document.querySelectorAll('#AddToCart, #DirectToCheckout'));

            if (buttons.length === 0) {
                this.log('Add to Cart / DirectToCheckout button not found');
                return;
            }

            buttons.forEach((btn) => {
                if (btn._planPickerIntercepted) return;
                btn._planPickerIntercepted = true;
                this.log('Intercepting button:', btn.id);

                btn.addEventListener('click', (e) => {
                    this.log('Button clicked:', btn.id, 'purchaseType:', this.purchaseType, 'directToCheckout:', this.directToCheckout);

                    const effectiveBoxes = this.getBoxes();
                    const currentBoxes = this.purchaseType === 'subscription' ? effectiveBoxes.subscription : effectiveBoxes.oneTime;
                    const selectedBox = currentBoxes[this.selectedQuantityIndex];

                    // Intercept for subscription orders (which include gifts).
                    // Intercept one-time orders only when directToCheckout is enabled.
                    const shouldIntercept =
                        (this.purchaseType === 'subscription' && this.selectedSellingPlan) ||
                        (this.purchaseType === 'oneTime' && this.directToCheckout);

                    if (!shouldIntercept) return; // Let the theme's standard form submit run.

                    e.preventDefault();

                    // If the theme rendered #DirectToCheckout, stop bubbling so the legacy
                    // delegated handler in assets/custom.js cannot run.
                    if (btn.id === 'DirectToCheckout') {
                        e.stopPropagation();
                    }

                    if (this.purchaseType === 'subscription') {
                        this.log('Adding subscription with', selectedBox.quantity, 'bottles and', selectedBox.gifts, 'gifts');
                    } else {
                        this.log('Adding one-time purchase with', selectedBox.quantity, 'bottles, redirecting to checkout');
                    }

                    this.addToCart(selectedBox.quantity);
                });
            });
        }, 500);
    }

    // Calculate discounted price
    calculatePrice(basePrice, discountPercent) {
        return basePrice * (1 - discountPercent / 100);
    }

    // Get total gift value for unlocked gifts (max 4 gifts)
    getTotalGiftValue(giftsCount) {
        const maxGifts = Math.min(giftsCount, MAX_GIFTS);
        return this.freeGifts.slice(0, maxGifts).reduce((sum, g) => sum + g.value, 0);
    }

    // Get unlocked gift variant IDs for cart addition (max 4 gifts)
    getUnlockedGiftVariantIds(giftsCount) {
        const maxGifts = Math.min(giftsCount, MAX_GIFTS);
        return this.freeGifts
            .slice(0, maxGifts)
            .filter(gift => gift.variantId !== null)
            .map(gift => gift.variantId);
    }

    render() {
        if (!this.product || !this.selectedVariant || !this.availableSellingPlanGroups?.length) return;

        const effectiveBoxes = this.getBoxes();
        const currentBoxes = this.purchaseType === 'subscription' ? effectiveBoxes.subscription : effectiveBoxes.oneTime;
        const selectedBox = currentBoxes[this.selectedQuantityIndex];
        const basePrice = this.selectedVariant.price / 100;

        return html`
            <div class="plan-picker">
                <!-- Purchase Type Toggle -->
                ${this.renderPurchaseToggle(effectiveBoxes, basePrice)}

                <!-- Upsell Banner (one-time only) -->
                ${this.purchaseType === 'oneTime' ? this.renderUpsellBanner() : ''}

                <!-- Quantity Selector -->
                ${this.renderQuantitySection(currentBoxes, basePrice)}

                <!-- Free Gifts Section -->
                ${this.renderGiftsSection(selectedBox)}

                <!-- Benefits List (subscription only) -->
                ${this.purchaseType === 'subscription' ? this.renderBenefitsList() : ''}
            </div>
        `;
    }

    renderPurchaseToggle(effectiveBoxes, basePrice) {
        // Get the selected box for both subscription and one-time to calculate dynamic per-bottle prices
        const subscriptionBox = effectiveBoxes.subscription[this.selectedQuantityIndex];
        const oneTimeBox = effectiveBoxes.oneTime[this.selectedQuantityIndex];

        const subscriptionPricePerBottle = this.calculatePrice(basePrice, subscriptionBox.discount);
        const oneTimePricePerBottle = this.calculatePrice(basePrice, oneTimeBox.discount);

        return html`
            <div class="purchase-toggle">
                <div class="toggle-option ${this.purchaseType === 'subscription' ? 'active' : ''}"
                     @click="${() => this.setPurchaseType('subscription')}">
                    <div class="toggle-radio"></div>
                    <div class="toggle-content">
                        <span class="toggle-label">Flexible Plan:</span>
                        <span class="toggle-price">${this.moneyFormatter.format(subscriptionPricePerBottle)} per bottle</span>
                        <span class="toggle-gifts">+ FREE Gifts</span>
                    </div>
                </div>
                <div class="toggle-option ${this.purchaseType === 'oneTime' ? 'active' : ''}"
                     @click="${() => this.setPurchaseType('oneTime')}">
                    <div class="toggle-radio"></div>
                    <div class="toggle-content">
                        <span class="toggle-label">One-Time Purchase:</span>
                        <span class="toggle-price onetime">${this.moneyFormatter.format(oneTimePricePerBottle)} per bottle</span>
                    </div>
                </div>
            </div>
        `;
    }

    renderUpsellBanner() {
        const subBox = this.getBoxes().subscription[this.selectedQuantityIndex];
        const savings = subBox.discount;
        const giftValue = this.getTotalGiftValue(subBox.gifts);

        return html`
            <div class="upsell-banner">
                <div class="upsell-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="upsell-content">
                    <div class="upsell-heading">
                        Save <span class="upsell-highlight">${savings}%</span> on your order + get <span class="upsell-highlight green">FREE gifts worth £${giftValue}</span>
                    </div>
                    <div class="upsell-subtext">Switch to Flexible Plan and save on every order - skip or cancel anytime</div>
                </div>
            </div>
        `;
    }

    // Get bottle image based on quantity
    getBottleImage(quantity) {
        // Map quantity to appropriate image key
        const imageMap = {
            1: 'bottle_1',
            3: 'bottle_3',
            6: 'bottle_6',
            9: 'bottle_6'  // Use 6-bottle image for 9 as fallback
        };

        // Try to get the specific bottle image from metafields
        if (this.imageMetafields) {
            const specificImage = this.imageMetafields[imageMap[quantity]];
            if (specificImage) return specificImage;

            // Fallback to bottle_1 if specific image not found
            if (this.imageMetafields.bottle_1) return this.imageMetafields.bottle_1;
        }

        // Final fallback: use product's featured image
        if (this.product?.featured_image) {
            return this.product.featured_image;
        }
        if (this.product?.images?.[0]?.src) {
            return this.product.images[0].src;
        }

        return null;
    }

    // Get bottle supply days based on quantity
    getSupplyDays(quantity) {
        const bottleSupply = this.productMetafields?.bottle_supply || 30;
        return bottleSupply * quantity;
    }

    renderQuantitySection(currentBoxes, basePrice) {
        return html`
            <div class="quantity-section">
                <h3 class="section-title">Choose Your Quantity:</h3>
                <div class="quantity-grid">
                    ${Object.entries(currentBoxes).map(([index, box]) => {
                        const priceEach = this.calculatePrice(basePrice, box.discount);
                        const isBestValue = box.featured === 'BEST VALUE';
                        const isSelected = this.selectedQuantityIndex === parseInt(index);
                        const supplyDays = this.getSupplyDays(box.quantity);
                        const bottleImage = this.getBottleImage(box.quantity);

                        return html`
                            <div class="qty-option ${isSelected ? 'selected' : ''} ${!box.badge ? 'no-header' : ''} ${!box.featured ? 'no-footer' : ''}"
                                 @click="${() => this.selectQuantity(parseInt(index))}">
                                <!-- Discount header at top -->
                                ${box.badge ? html`
                                    <div class="qty-discount-header">${box.badge}</div>
                                ` : ''}

                                <div class="qty-content">
                                    <div class="qty-title">${box.quantity} Bottle${box.quantity > 1 ? 's' : ''}</div>
                                    <div class="qty-supply">${supplyDays} day supply</div>
                                    ${bottleImage ? html`
                                        <img class="qty-image" src="${bottleImage}" alt="${box.quantity} bottle${box.quantity > 1 ? 's' : ''}" loading="lazy">
                                    ` : ''}
                                    <div class="qty-price-each"><strong>${this.moneyFormatter.format(priceEach)}</strong> each</div>
                                </div>

                                <!-- Featured badge at bottom (MOST POPULAR / BEST VALUE) -->
                                ${box.featured ? html`
                                    <div class="qty-featured ${isBestValue ? 'best-value' : ''}">${box.featured}</div>
                                ` : ''}
                            </div>
                        `;
                    })}
                </div>
            </div>
        `;
    }

    renderGiftsSection(selectedBox) {
        const giftsToUnlock = this.purchaseType === 'subscription' ? selectedBox.gifts : 0;
        const totalGifts = Math.min(this.freeGifts.length, MAX_GIFTS);
        const giftsToShow = this.freeGifts.slice(0, MAX_GIFTS);

        return html`
            <div class="gifts-section">
                <div class="gifts-header">
                    <span class="gifts-title">
                        <span>🎁</span>
                        <span>FREE Gifts With Your First Order</span>
                    </span>
                    ${this.purchaseType === 'subscription'
                        ? html`<span class="gifts-count">UNLOCKED: ${giftsToUnlock} of ${totalGifts}</span>`
                        : html`<span class="gifts-count">Unlock on Flexible Plan</span>`
                    }
                </div>

                <div class="gifts-grid">
                    ${giftsToShow.map((gift, index) => {
                        const isUnlocked = this.purchaseType === 'subscription' && index < giftsToUnlock;
                        return html`
                            <div class="gift-item ${isUnlocked ? '' : 'locked'}">
                                <div class="gift-image-wrapper">
                                    <img class="gift-image" src="${gift.image}" alt="${gift.name}" loading="lazy">
                                    <span class="gift-value">
                                        <span class="value-price">£${gift.value}</span>
                                        <span class="value-label">Value!</span>
                                    </span>
                                    ${!isUnlocked ? html`
                                        <div class="gift-lock-overlay">
                                            <svg class="lock-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="3" y="11" width="18" height="11" rx="2" fill="currentColor"/>
                                                <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                            </svg>
                                        </div>
                                    ` : ''}
                                </div>
                                <span class="gift-name"><span class="free-label">FREE</span> ${gift.name}</span>
                            </div>
                        `;
                    })}
                </div>
            </div>
        `;
    }

    renderBenefitsList() {
        const benefits = [
            'Skip or cancel at any time',
            'Fast & Free Delivery',
            'Covered by our 60-day money-back guarantee'
        ];

        return html`
            <div class="benefits-list">
                ${benefits.map(benefit => html`
                    <div class="benefit-item">
                        <span class="benefit-check">
                            <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
                                <path d="M1 5.625L3.8125 8.4375L8.5 1.875" stroke="currentColor" stroke-width="1.875" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </span>
                        <span>${benefit}</span>
                    </div>
                `)}
            </div>
        `;
    }

    // Calculate estimated delivery dates (2-3 working days from today)
    getDeliveryDates() {
        const today = new Date();

        // Add working days (skip weekends)
        const addWorkingDays = (startDate, days) => {
            const result = new Date(startDate);
            let addedDays = 0;

            while (addedDays < days) {
                result.setDate(result.getDate() + 1);
                const dayOfWeek = result.getDay();
                // Skip Saturday (6) and Sunday (0)
                if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                    addedDays++;
                }
            }
            return result;
        };

        const minDate = addWorkingDays(today, 2);
        const maxDate = addWorkingDays(today, 3);

        // Format dates as "6 Feb"
        const formatDate = (date) => {
            const day = date.getDate();
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${day} ${months[date.getMonth()]}`;
        };

        return {
            min: formatDate(minDate),
            max: formatDate(maxDate)
        };
    }

    renderDeliveryEstimate() {
        const dates = this.getDeliveryDates();

        return html`
            <div class="delivery-estimate">
                <svg class="delivery-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 7V12L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="delivery-text">
                    Estimated Delivery: <span class="delivery-dates">${dates.min} - ${dates.max}</span>
                </span>
            </div>
        `;
    }

    selectQuantity(index) {
        this.selectedQuantityIndex = index;

        // Auto-select matching selling plan for subscription
        // 1 bottle (index 1) → 1 month plan (index 0)
        // 3 bottles (index 2) → 3 months plan (index 1)
        // 6 bottles (index 3) → 6 months plan (index 2)
        if (this.purchaseType === 'subscription' && this.selectedSellingPlanGroup?.selling_plans?.length > 0) {
            const sellingPlanIndex = index - 1; // Convert quantity index to plan index
            if (sellingPlanIndex >= 0 && sellingPlanIndex < this.selectedSellingPlanGroup.selling_plans.length) {
                this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans[sellingPlanIndex];
                this.log('Auto-selected selling plan for quantity index', index, ':', this.selectedSellingPlan?.name);
            }
        }

        this.updateForm();
    }

    setPurchaseType(type) {
        this.purchaseType = type;

        if (type === 'subscription') {
            // Select first selling plan group
            if (this.availableSellingPlanGroups?.length > 0) {
                this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];

                // Select the selling plan that matches the current quantity
                // 1 bottle (index 1) → 1 month plan (index 0)
                // 3 bottles (index 2) → 3 months plan (index 1)
                // 6 bottles (index 3) → 6 months plan (index 2)
                if (this.selectedSellingPlanGroup.selling_plans?.length > 0) {
                    const sellingPlanIndex = this.selectedQuantityIndex - 1;
                    if (sellingPlanIndex >= 0 && sellingPlanIndex < this.selectedSellingPlanGroup.selling_plans.length) {
                        this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans[sellingPlanIndex];
                    } else {
                        // Fallback to first plan if index out of range
                        this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans[0];
                    }
                    this.log('Subscription selected, selling plan:', this.selectedSellingPlan?.name);
                }
            }
        } else {
            // Clear selling plan for one-time
            this.selectedSellingPlanGroup = null;
            this.selectedSellingPlan = null;
        }

        this.updateForm();
    }

    switchToSubscription() {
        this.setPurchaseType('subscription');
    }

    handleAddToCart(selectedBox) {
        // For landing page, we handle the cart addition ourselves (including gifts)
        // instead of using the external form, to ensure gifts are added correctly

        if (this.purchaseType === 'subscription' && this.selectedSellingPlan) {
            // Use our own addToCart which includes gift logic
            this.addToCart(selectedBox.quantity);
        } else {
            // For one-time purchases, we can use the standard form flow
            const parentEl = this.parentNode;
            const quantityInput = parentEl?.querySelector('[name="quantity"]');
            if (quantityInput) {
                quantityInput.value = selectedBox.quantity;
                quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Update selling plan input
            const sellingPlanInput = this.querySelector('input[name=selling_plan]');
            if (sellingPlanInput) {
                sellingPlanInput.value = '';
            }

            // Trigger form submit via the external Add to Cart button
            const addToCartBtn = parentEl?.querySelector('#AddToCart, #DirectToCheckout');
            if (addToCartBtn) {
                addToCartBtn.click();
            } else {
                // Fallback: use our addToCart
                this.addToCart(selectedBox.quantity);
            }
        }
    }

    updated = (changed) => {
        if (changed.has('product')) {
            if (!this.product) {
                return;
            }

            if (!this.hasSellingPlanData(this.product)) {
                this.ensureProductData();
                return;
            }

            this.key = this.key ? this.key : this.product.id;

            this.skioSellingPlanGroups = (this.product.selling_plan_groups || []).filter(
                (selling_plan_group) => selling_plan_group.app_id === 'SKIO'
            );
        }

        if (changed.has('selectedVariant')) {
            if (!this.selectedVariant) {
                return;
            }

            if (!this.selectedVariant.selling_plan_allocations?.length) {
                this.ensureProductData();
                return;
            }

            const targetGroupId = 'd1c31b7d875f371afdda85ee0906e6c15aee403a';

            const targetGroupDefinition = (this.skioSellingPlanGroups || []).find(
                group => group.id === targetGroupId
            );

            if (targetGroupDefinition) {
                const allocatedPlanIdsForTargetGroup = new Set(
                    this.selectedVariant.selling_plan_allocations
                        .filter(alloc => alloc.selling_plan_group_id === targetGroupId)
                        .map(alloc => String(alloc.selling_plan_id))
                );

                const allowedPlanIds = this.getAllowedSellingPlanIds();
                const allowedPlanIdList = this.getAllowedSellingPlanIdList();

                const availablePlans = targetGroupDefinition.selling_plans.filter((plan) => {
                    const planId = String(plan.id);
                    return allocatedPlanIdsForTargetGroup.has(planId) && allowedPlanIds.has(planId);
                });

                availablePlans.sort((a, b) => {
                    const aIndex = allowedPlanIdList.indexOf(String(a.id));
                    const bIndex = allowedPlanIdList.indexOf(String(b.id));
                    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
                });

                if (availablePlans.length > 0) {
                    this.availableSellingPlanGroups = [
                        {
                            ...targetGroupDefinition,
                            selling_plans: availablePlans
                        }
                    ];
                } else {
                    this.availableSellingPlanGroups = [];
                }
            } else {
                this.availableSellingPlanGroups = [];
            }

            // Initialize selection based on startSubscription setting
            if (this.availableSellingPlanGroups?.length > 0) {
                const shouldSelectSubscription = this.startSubscription === true || this.product.requires_selling_plan === true;

                if (shouldSelectSubscription) {
                    this.purchaseType = 'subscription';
                    this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];

                    // Select the selling plan that matches the current quantity
                    // Default is index 3 (6 bottles) → plan index 2 (6 months)
                    if (this.selectedSellingPlanGroup.selling_plans.length > 0) {
                        const sellingPlanIndex = this.selectedQuantityIndex - 1;
                        if (sellingPlanIndex >= 0 && sellingPlanIndex < this.selectedSellingPlanGroup.selling_plans.length) {
                            this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans[sellingPlanIndex];
                        } else {
                            this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans[0];
                        }
                        this.log('Initial selling plan selection:', this.selectedSellingPlan?.name);
                    }
                } else {
                    this.purchaseType = 'oneTime';
                    this.selectedSellingPlanGroup = null;
                    this.selectedSellingPlan = null;
                }
            }
        }

        this.updateForm();
    }

    updateForm() {
        const effectiveBoxes = this.getBoxes();
        const currentBoxes = this.purchaseType === 'subscription' ? effectiveBoxes.subscription : effectiveBoxes.oneTime;
        const selectedBox = currentBoxes[this.selectedQuantityIndex];

        if (!selectedBox || !this.selectedVariant) return;

        // Update selling plan input
        const sellingPlanInput = this.querySelector('input[name=selling_plan]');
        if (sellingPlanInput) {
            if (this.purchaseType === 'subscription' && this.selectedSellingPlan) {
                sellingPlanInput.value = this.selectedSellingPlan.id;
            } else {
                sellingPlanInput.value = '';
            }
        }

        // Update quantity input
        const parentEl = this.parentNode;
        const quantityInput = parentEl?.querySelector('[name="quantity"]');
        if (quantityInput) {
            quantityInput.value = selectedBox.quantity;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Also update quantity-selector input if exists
        const quantitySelector = parentEl?.querySelector('quantity-selector');
        const quantitySelectorInput = quantitySelector?.querySelector('input');
        if (quantitySelectorInput) {
            quantitySelectorInput.value = selectedBox.quantity;
            quantitySelectorInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update external submit button text (AddToCart or DirectToCheckout)
        const addToCartButton = parentEl?.querySelector('#AddToCart .loader-button__text, #DirectToCheckout .loader-button__text');
        if (addToCartButton) {
            const basePrice = this.selectedVariant.price / 100;
            const priceEach = this.calculatePrice(basePrice, selectedBox.discount);
            const priceTotal = priceEach * selectedBox.quantity;
            const originalTotal = basePrice * selectedBox.quantity;

            if (this.purchaseType === 'subscription') {
                addToCartButton.innerHTML = `SUBSCRIBE | <span class="loader-button__price">${this.moneyFormatter.format(priceTotal)} <span style="text-decoration: line-through; opacity: 0.7;">${this.moneyFormatter.format(originalTotal)}</span></span>`;
            } else {
                if (selectedBox.discount > 0) {
                    addToCartButton.innerHTML = `ADD TO CART | <span class="loader-button__price">${this.moneyFormatter.format(priceTotal)} <span style="text-decoration: line-through; opacity: 0.7;">${this.moneyFormatter.format(originalTotal)}</span></span>`;
                } else {
                    addToCartButton.innerHTML = `ADD TO CART | <span class="loader-button__price">${this.moneyFormatter.format(priceTotal)}</span>`;
                }
            }
        }
    }

    log = (...args) => {
        args.unshift('%c[skio]', 'color: #8770f2;');
        console.log.apply(console, args);
    }

    error = (...args) =>  {
        args.unshift('%c [skio]', 'color: #ff0000');
        console.error.apply(console, args);
    }

    setAddingToCart(isAdding) {
        this._addingToCart = isAdding;
        const addToCartBtn = document.querySelector('#AddToCart, #DirectToCheckout');
        if (!addToCartBtn) return;
        addToCartBtn.disabled = isAdding;
        addToCartBtn.toggleAttribute('aria-busy', isAdding);
        addToCartBtn.classList.toggle('loading', isAdding); // Also drives the CSS loading animation
    }

    // The buy box is a single choice: picking again (double-click, or back from checkout and choose another
    // box) should replace the earlier selection, not stack a second set of bottles and gifts in the cart.
    async removePreviousLandingPageLines() {
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((res) => res.json());
        const updates = {};
        cart.items.forEach((item) => {
            const props = item.properties || {};
            const isPreviousSelection = props._source === 'landing-page' && item.product_id === this.product.id;
            const isPickerGift = props._gift_with_subscription === 'true';
            if (isPreviousSelection || isPickerGift) updates[item.key] = 0;
        });
        if (Object.keys(updates).length === 0) return;

        this.log('Replacing previous landing page selection:', updates);
        const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error('Failed to replace previous selection');
    }

    async addToCart(quantity) {
        if (this._addingToCart) {
            this.log('Add to cart already in progress, ignoring repeat click');
            return;
        }
        this.setAddingToCart(true);

        // Build items array
        const items = [{
            id: this.selectedVariant.id,
            quantity: quantity,
            ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id }),
            properties: {
                '_source': 'landing-page'
            }
        }];

        let addingGifts = false;
        let giftCount = 0;

        // Add gifts for subscriptions
        if (this.purchaseType === 'subscription' && this.selectedSellingPlan) {
            const currentBoxes = this.getBoxes().subscription;
            const selectedBox = currentBoxes[this.selectedQuantityIndex];
            const giftsToAdd = selectedBox?.gifts || 0;

            if (giftsToAdd > 0) {
                const giftVariantIds = this.getUnlockedGiftVariantIds(giftsToAdd);
                giftCount = giftVariantIds.length;

                if (giftVariantIds.length > 0) {
                    addingGifts = true;
                    giftVariantIds.forEach(variantId => {
                        items.push({
                            id: variantId,
                            quantity: 1,
                            properties: {
                                '_free_gift': 'true',
                                '_gift_with_subscription': 'true'
                            }
                        });
                    });
                }
            }
        }

        try {
            await this.removePreviousLandingPageLines();

            // OPTIMIZATION: Don't request sections if going directly to checkout - saves server-side rendering time
            const requestBody = { items };
            if (!this.directToCheckout) {
                requestBody.sections = ['mini-cart'];
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Be explicit: attribution depends on the cart session cookie set by Shopify.
                credentials: 'same-origin',
                body: JSON.stringify(requestBody)
            });

            // OPTIMIZATION: For checkout flow, just check success and redirect immediately
            if (this.directToCheckout) {
                if (response.ok) {
                    // Calculate values for tracking
                    const effectiveBoxes = this.getBoxes();
                    const currentBoxes = this.purchaseType === 'subscription' ? effectiveBoxes.subscription : effectiveBoxes.oneTime;
                    const selectedBox = currentBoxes[this.selectedQuantityIndex];
                    const basePrice = this.selectedVariant.price / 100;
                    const priceEach = this.calculatePrice(basePrice, selectedBox.discount);
                    const totalValue = priceEach * quantity;

                    // Fire tracking events BEFORE redirect
                    // 1. DataLayer add_to_cart event (for GA4, GTM, Triple Whale)
                    window.dataLayer = window.dataLayer || [];
                    if (window.dataLayer) {
                        window.dataLayer.push({
                            'event': 'add_to_cart',
                            'ecommerce': {
                                'currency': this.currency,
                                'value': totalValue,
                                'items': [{
                                    // Product ID (not variant ID), for GA4/Triple Whale consistency
                                    'item_id': String(this.product.id),
                                    'item_name': this.product.title,
                                    'item_variant': this.selectedVariant.title,
                                    'price': priceEach,
                                    'quantity': quantity,
                                    'item_brand': this.product.vendor || 'Purolabs'
                                }]
                            }
                        });
                        this.log('Fired dataLayer add_to_cart event');
                    }

                    // 2. Facebook Pixel - SKIPPED: Elevar automatically fires fbq('trackSingle', 'AddToCart')
                    // when it intercepts the /cart/add.js fetch. Manual fbq() call would create duplicate events.

                    // 3. TikTok Pixel AddToCart event
                    if (typeof ttq === 'object' && ttq.track) {
                        ttq.track('AddToCart', {
                            content_id: String(this.selectedVariant.id),
                            content_name: this.product.title,
                            content_type: 'product',
                            quantity: quantity,
                            price: priceEach,
                            value: totalValue,
                            currency: this.currency
                        });
                        this.log('Fired TikTok Pixel AddToCart event');
                    }

                    // 4. Triple Whale TriplePixel AddToCart event
                    if (typeof TriplePixel === 'function') {
                        TriplePixel('AddToCart', {
                            item_id: String(this.product.id),
                            item_name: this.product.title,
                            price: priceEach,
                            quantity: quantity,
                            currency: this.currency
                        });
                        this.log('Fired Triple Whale AddToCart event');
                    }

                    // 5. Dispatch custom event for any other listeners
                    this.dispatchEvent(new CustomEvent('skio::added-to-cart', {
                        bubbles: true,
                        composed: true,
                        detail: {
                            items: items,
                            key: this.key,
                            includesGifts: addingGifts,
                            value: totalValue,
                            currency: this.currency
                        }
                    }));

                    // CRITICAL: Preserve URL parameters (UTM, fbclid, gclid, ttclid, etc.) for attribution
                    const currentParams = new URLSearchParams(window.location.search);

                    // Add discount code if we have gifts
                    if (addingGifts && giftCount > 0) {
                        currentParams.set('discount', 'FREEGIFTS');
                    }

                    // Build checkout URL preserving all tracking parameters
                    const paramString = currentParams.toString();
                    const checkoutUrl = '/checkout' + (paramString ? '?' + paramString : '');

                    this.log('Redirecting to checkout with preserved params:', checkoutUrl);

                    // CRITICAL: Delay redirect to give tracking pixels time to fire
                    // Native link clicks work because the browser queues the navigation,
                    // giving tracking scripts time to send their beacons.
                    // We replicate this by waiting 500ms before redirecting.
                    setTimeout(() => {
                        window.location.href = checkoutUrl;
                    }, 500);
                    return;
                } else {
                    throw new Error('Failed to add to cart');
                }
            }

            // For mini-cart flow, parse response and continue
            const addResponse = await response.json();
            this.log("Added items to cart:", addResponse);

            // Dispatch tracking event
            this.dispatchEvent(new CustomEvent('skio::added-to-cart', {
                bubbles: true,
                composed: true,
                detail: { response: addResponse, key: this.key, includesGifts: addingGifts }
            }));

            // Apply discount code for mini-cart flow
            if (addingGifts && giftCount > 0) {
                try {
                    await fetch('/discount/FREEGIFTS');
                    this.log('Applied FREEGIFTS discount code');
                } catch (e) {
                    this.log('Note: FREEGIFTS discount code may not be configured');
                }
            }

            // Open mini-cart drawer
            const cartResponse = await fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}.js`);
            const cartContent = await cartResponse.json();

            const sectionResponse = await fetch('/cart?section_id=mini-cart');
            const sectionHtml = await sectionResponse.text();

            cartContent.sections = { 'mini-cart': sectionHtml };

            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
                bubbles: true,
                detail: { cart: cartContent }
            }));

            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { cart: cartContent, openMiniCart: true }
            }));

            this.log('Mini-cart drawer should now be open');

            // Remove loading state after mini-cart opens
            this.setAddingToCart(false);

        } catch (error) {
            this.error('Error adding to cart:', error);
            // Remove loading state on error
            this.setAddingToCart(false);
        }
        // Note: For checkout flow, we don't remove loading state because we're redirecting
        // and want the button to stay in loading state until the page navigates
    }

    fetchProduct = (handle) => {
        return fetch(`/products/${ handle }.js`)
        .then((response) => response.json())
        .then((product) => {
            this.product = product;
            this.selectedVariant = product.variants[0];

            return product;
        });
    }
}

customElements.define('purolabs-plan-picker-landing', PurolabsPlanPickerComponent);
